import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { logout } from './authSlice';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';

const defaultMeta = {
  page: 1,
  page_size: 10,
  total_items: 0,
  total_pages: 0
};

const formatDateForQuery = (date) => date.toISOString().split('T')[0];

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return {
    start: formatDateForQuery(start),
    end: formatDateForQuery(end)
  };
};

const defaultDateRange = getDefaultDateRange();

const defaultQuery = {
  page: 1,
  pageSize: 10,
  startDate: defaultDateRange.start,
  endDate: defaultDateRange.end,
  originStation: 'ALL',
  destinationStation: 'ALL',
  purchaseStatus: 'ALL',
  transactionStatus: 'ALL',
  search: '',
  sortBy: 'transaction_datetime',
  sortDirection: 'desc'
};

const initialState = {
  items: [],
  meta: defaultMeta,
  loading: false,
  error: null,
  query: defaultQuery,
  exportStatus: 'idle',
  exportError: null,
  stations: [],
  stationsLoading: false,
  stationsError: null
};

const buildAuthHeaders = (getState, extra = {}) => {
  const { auth } = getState();
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...extra
  };

  if (auth?.accessToken) {
    headers.Authorization = `Bearer ${auth.accessToken}`;
  }

  return headers;
};

const handleUnauthorized = (dispatch) => {
  dispatch(logout());
  if (typeof window !== 'undefined') {
    window.location.replace('/login');
  }
};

const handleApiResponse = async (response, dispatch) => {
  if (response.status === 401) {
    handleUnauthorized(dispatch);
    throw new Error('Unauthorized');
  }

  const contentType = response.headers.get('content-type');
  let data;

  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const message =
      (data &&
        typeof data === 'object' &&
        (data.resultDesc || data.resultMessage || data.message)) ||
      response.statusText ||
      'Request failed';
    throw new Error(message);
  }

  if (
    data &&
    typeof data === 'object' &&
    Object.prototype.hasOwnProperty.call(data, 'resultCode') &&
    Number(data.resultCode) !== 200
  ) {
    if (Number(data.resultCode) === 401) {
      handleUnauthorized(dispatch);
    }
    const message = data.resultDesc || data.resultMessage || data.message || 'Request failed';
    throw new Error(message);
  }

  if (data && typeof data === 'object' && Number(data.resultCode) === 401) {
    handleUnauthorized(dispatch);
    throw new Error(data.resultDesc || data.resultMessage || data.message || 'Unauthorized');
  }

  return data;
};

const buildQueryString = (params = {}) => {
  const merged = {
    ...defaultQuery,
    ...params
  };

  const searchParams = new URLSearchParams();

  if (merged.page) searchParams.set('page', merged.page);
  if (merged.pageSize) searchParams.set('page_size', merged.pageSize);
  if (merged.startDate) searchParams.set('start_date', merged.startDate);
  if (merged.endDate) searchParams.set('end_date', merged.endDate);
  if (merged.originStation && merged.originStation !== 'ALL') {
    searchParams.set('origin_station_code', merged.originStation);
  }
  if (merged.destinationStation && merged.destinationStation !== 'ALL') {
    searchParams.set('destination_station_code', merged.destinationStation);
  }
  if (merged.purchaseStatus && merged.purchaseStatus !== 'ALL') {
    searchParams.set('status', merged.purchaseStatus);
  }
  if (merged.transactionStatus && merged.transactionStatus !== 'ALL') {
    searchParams.set('transaction_status', merged.transactionStatus);
  }
  if (merged.search) searchParams.set('search', merged.search);
  if (merged.sortBy) searchParams.set('sort_by', merged.sortBy);
  if (merged.sortDirection) searchParams.set('sort_direction', merged.sortDirection);

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

const normalizeStation = (station) => {
  if (!station) {
    return { code: '', name: '', order_no: Number.POSITIVE_INFINITY };
  }

  if (typeof station === 'string') {
    const code = station.toUpperCase();
    return { code, name: station, order_no: Number.POSITIVE_INFINITY };
  }

  const code =
    (station.code || station.station_code || station.id || '').toString().toUpperCase();
  const name =
    station.name ||
    station.station_name ||
    station.label ||
    station.value ||
    station.display_name ||
    code;
  const orderNo = Number(station.order_no ?? station.orderNo ?? Number.POSITIVE_INFINITY);

  return {
    code,
    name,
    order_no: Number.isFinite(orderNo) ? orderNo : Number.POSITIVE_INFINITY
  };
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const transformItem = (item) => {
  const partner =
    item.partner ||
    item.source_app ||
    item.app_source ||
    {
      id: item.source_app_id ?? null,
      name: item.source_app_name || '',
      quota_type: item.quota_type || ''
    };

  const originStation = normalizeStation(
    item.origin_station ||
      item.originStation ||
      item.origin_station_detail ||
      item.origin_station_info || { code: item.origin_station_code, name: item.origin_station_name }
  );
  const destinationStation = normalizeStation(
    item.destination_station ||
      item.destinationStation ||
      item.destination_station_detail ||
      item.destination_station_info || {
        code: item.destination_station_code,
        name: item.destination_station_name
      }
  );

  const periodStart =
    partner.start_datetime ||
    partner.start_datetime_utc ||
    partner.startDate ||
    partner.start_date ||
    item.start_datetime ||
    null;
  const periodEnd =
    partner.end_datetime ||
    partner.end_datetime_utc ||
    partner.endDate ||
    partner.end_date ||
    item.end_datetime ||
    null;

  return {
    id: item.id,
    transaction_datetime:
      item.transaction_datetime || item.transactionDatetime || item.created_datetime || null,
    partner: {
      id: partner.id ?? item.source_app_id ?? null,
      name: partner.name || item.source_app_name || 'Unknown',
      quota_type: partner.quota_type || item.quota_type || ''
    },
    msisdn: item.msisdn || '',
    origin: originStation,
    destination: destinationStation,
    ticket_total: toNumber(item.ticket_total ?? item.total, 0),
    total_fare: toNumber(item.total_fare, 0),
    trip: item.trip || item.trip_type || '',
    quota_usage: toNumber(item.quota_usage, 0),
    quota_type: partner.quota_type || item.quota_type || '',
    period: {
      start: periodStart,
      end: periodEnd
    },
    transaction_status: item.transaction_status || item.transactionStatus || '',
    status: item.status,
    status_desc: item.status_desc || item.status_description || null
  };
};

export const fetchTransactionLogStations = createAsyncThunk(
  'transactionLogs/fetchStations',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stations`, {
        method: 'GET',
        headers: buildAuthHeaders(getState)
      });
      const data = await handleApiResponse(response, dispatch);
      const stationData = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.resultData?.data)
        ? data.resultData.data
        : Array.isArray(data?.resultData)
        ? data.resultData
        : data?.stations && Array.isArray(data.stations)
        ? data.stations
        : [];

      return stationData
        .map((station) => normalizeStation(station))
        .filter((station) => station.code)
        .sort((a, b) => {
          const orderA = Number.isFinite(a.order_no) ? a.order_no : Number.MAX_SAFE_INTEGER;
          const orderB = Number.isFinite(b.order_no) ? b.order_no : Number.MAX_SAFE_INTEGER;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          return a.code.localeCompare(b.code);
        });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load stations.');
    }
  }
);

export const fetchTransactionLogs = createAsyncThunk(
  'transactionLogs/fetchLogs',
  async (params, { getState, dispatch, rejectWithValue }) => {
    try {
      const queryString = buildQueryString(params);
      const response = await fetch(`${API_BASE_URL}/api/transaction-log${queryString}`, {
        method: 'GET',
        headers: buildAuthHeaders(getState)
      });

      const data = await handleApiResponse(response, dispatch);
      const rawItems = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.resultData?.data)
        ? data.resultData.data
        : Array.isArray(data?.resultData)
        ? data.resultData
        : [];
      const items = rawItems.map(transformItem);

      const rawMeta = data?.meta || data?.resultData?.meta;
      const meta = rawMeta
        ? {
            page: Number(rawMeta.page ?? params?.page ?? defaultMeta.page),
            page_size: Number(rawMeta.page_size ?? params?.pageSize ?? defaultMeta.page_size),
            total_items: Number(rawMeta.total_items ?? defaultMeta.total_items),
            total_pages: Number(rawMeta.total_pages ?? defaultMeta.total_pages)
          }
        : {
            page: params?.page ?? defaultMeta.page,
            page_size: params?.pageSize ?? defaultMeta.page_size,
            total_items: defaultMeta.total_items,
            total_pages: defaultMeta.total_pages
          };

      return {
        items,
        meta,
        query: {
          page: params?.page ?? defaultQuery.page,
          pageSize: params?.pageSize ?? defaultQuery.pageSize,
          startDate: params?.startDate ?? defaultQuery.startDate,
          endDate: params?.endDate ?? defaultQuery.endDate,
          originStation: params?.originStation ?? defaultQuery.originStation,
          destinationStation: params?.destinationStation ?? defaultQuery.destinationStation,
          purchaseStatus: params?.purchaseStatus ?? defaultQuery.purchaseStatus,
          transactionStatus: params?.transactionStatus ?? defaultQuery.transactionStatus,
          search: params?.search ?? defaultQuery.search,
          sortBy: params?.sortBy ?? defaultQuery.sortBy,
          sortDirection: params?.sortDirection ?? defaultQuery.sortDirection
        }
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch transaction logs.');
    }
  }
);

export const exportTransactionLogs = createAsyncThunk(
  'transactionLogs/exportLogs',
  async (params, { getState, dispatch, rejectWithValue }) => {
    try {
      const searchParams = new URLSearchParams();
      const merged = { ...defaultQuery, ...params };
      if (merged.startDate) searchParams.set('start_date', merged.startDate);
      if (merged.endDate) searchParams.set('end_date', merged.endDate);
      if (merged.originStation && merged.originStation !== 'ALL') {
        searchParams.set('origin_station_code', merged.originStation);
      }
      if (merged.destinationStation && merged.destinationStation !== 'ALL') {
        searchParams.set('destination_station_code', merged.destinationStation);
      }
      if (merged.purchaseStatus && merged.purchaseStatus !== 'ALL') {
        searchParams.set('status', merged.purchaseStatus);
      }
      if (merged.transactionStatus && merged.transactionStatus !== 'ALL') {
        searchParams.set('transaction_status', merged.transactionStatus);
      }
      if (merged.search) searchParams.set('search', merged.search);
      if (merged.sortBy) searchParams.set('sort_by', merged.sortBy);
      if (merged.sortDirection) searchParams.set('sort_direction', merged.sortDirection);
      searchParams.set('all', 'true');

      const queryString = searchParams.toString();
      const exportUrl =
        `${API_BASE_URL}/api/transaction-log/export` + (queryString ? `?${queryString}` : '');

      const response = await fetch(exportUrl, {
        method: 'GET',
        headers: buildAuthHeaders(getState, {
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/json'
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(dispatch);
        }
        const message = await response.text();
        throw new Error(message || response.statusText || 'Failed to export transaction logs.');
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const payload = await response.json();
        if (Number(payload?.resultCode) === 401) {
          handleUnauthorized(dispatch);
        }
        if (Number(payload?.resultCode) !== 200) {
          throw new Error(
            payload?.resultDesc ||
              payload?.resultMessage ||
              payload?.message ||
              'Failed to export transaction logs.'
          );
        }
        throw new Error(payload?.resultDesc || 'No file available for download.');
      }

      const blob = await response.blob();
      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const disposition = response.headers.get('content-disposition') || '';
        const match = disposition.match(/filename="?(.+?)"?$/i);
        const filename = match ? match[1] : `transaction-log-${new Date().toISOString().slice(0, 10)}.xlsx`;

        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
      }

      return true;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to export transaction logs.');
    }
  }
);

const transactionLogsSlice = createSlice({
  name: 'transactionLogs',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTransactionLogStations.pending, (state) => {
        state.stationsLoading = true;
        state.stationsError = null;
      })
      .addCase(fetchTransactionLogStations.fulfilled, (state, action) => {
        state.stationsLoading = false;
        state.stations = action.payload;
      })
      .addCase(fetchTransactionLogStations.rejected, (state, action) => {
        state.stationsLoading = false;
        state.stationsError = action.payload || 'Failed to load stations.';
      })
      .addCase(fetchTransactionLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactionLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.meta = action.payload.meta;
        state.query = action.payload.query;
      })
      .addCase(fetchTransactionLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch transaction logs.';
      })
      .addCase(exportTransactionLogs.pending, (state) => {
        state.exportStatus = 'loading';
        state.exportError = null;
      })
      .addCase(exportTransactionLogs.fulfilled, (state) => {
        state.exportStatus = 'succeeded';
      })
      .addCase(exportTransactionLogs.rejected, (state, action) => {
        state.exportStatus = 'failed';
        state.exportError = action.payload || 'Failed to export transaction logs.';
      });
  }
});

export default transactionLogsSlice.reducer;
